import { MutationOptions, QueryOptions } from '@apollo/client/core';
import { GqlFields, IFilterSelectValue } from 'ngx-register-base';
import { format } from 'date-fns';
import { EDatePattern } from '../constants/date-time.constants';
import { PrizmDayRange } from '@prizm-ui/components';

export const getGQLIlike = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { _ilike: `%${value}%` } } : undefined;

const REGEX_SPECIAL_CHARACTERS_PATTERN = /[$()*+.?[\\\]^{|}]/g;
const E_LETTER_VARIANTS_PATTERN = /[её]/gi;

export const getELetterTolerantSearchPattern = (query: string): string =>
  query
    .replaceAll(REGEX_SPECIAL_CHARACTERS_PATTERN, '\\$&')
    .replaceAll(E_LETTER_VARIANTS_PATTERN, '[её]');

interface CustomMutationOptions extends Omit<MutationOptions, 'fetchPolicy'> {
  fetchPolicy?: 'no-cache';
}

export interface CustomQueryOptions extends Omit<QueryOptions, 'fetchPolicy'> {
  fetchPolicy?: 'no-cache';
}

type FetchPolicyOptions =
  | Omit<CustomMutationOptions, 'fetchPolicy'>
  | Omit<CustomQueryOptions, 'fetchPolicy'>;

function noCache(options: FetchPolicyOptions): CustomMutationOptions | CustomQueryOptions {
  return {
    ...options,
    fetchPolicy: 'no-cache',
  };
}

export function noCacheMutation(
  options: Omit<CustomMutationOptions, 'fetchPolicy'>
): CustomMutationOptions {
  return noCache(options) as CustomMutationOptions;
}

export function noCacheQuery(options: Omit<CustomQueryOptions, 'fetchPolicy'>): CustomQueryOptions {
  return noCache(options) as CustomQueryOptions;
}

/**
 * Формирует значение postgres-массива (`_text`/`_uuid`) для аргумента hasura-функции.
 */
export const getGQLArrayArgValue = (
  value: IFilterSelectValue[] | null,
  searchKey: string,
  field: 'id' | 'code' = 'id'
): GqlFields | undefined =>
  value?.length
    ? {
        [searchKey]: `{"${value.map((item) => item[field]).join('", "')}"}`,
      }
    : undefined;

export const getGQLDayRangeValue = (
  value: PrizmDayRange | null,
  searchKey: string
): GqlFields | undefined => {
  if (!value) {
    return;
  }
  const setDateGreater = (date: Date) => {
    date.setDate(date.getDate() + 1);
    return date;
  };
  const dateTo = setDateGreater(value.to.toLocalNativeDate());
  const from = format(value.from.toLocalNativeDate(), EDatePattern.YEAR_MONTH_DAY);
  const to = format(dateTo, EDatePattern.YEAR_MONTH_DAY);

  return {
    [searchKey]: {
      _gte: from,
      _lt: to,
    },
  };
};
